import React, { useState } from 'react';
import { uniqueId } from 'lodash';
import { Flex, Icon, Box, styled, css } from '@goodrx/matisse';
import { UniversalLink } from 'app/components/links/UniversalLink';
import { DefaultMessage, MessageProps } from 'app/components/widgets/chat/message-types';
import { onEducationMessageViewed, onEducationMessageClicked } from 'app/lib/analytics';
import { useIntersectionObserver } from 'app/hooks/useIntersectionObserver';
import { useAppContext } from 'app/config/AppContext';

const Wrapper = styled.div`
  ${({ theme }: any) => css`
    border-radius: ${theme.radius}px;
    border: 1px solid ${theme.colors.greyLight};
    margin: ${theme.space[2]}px 0;
    min-height: 94px;
    padding: ${theme.space[2]}px;
    max-width: 280px;
  `};
`;

const Title = styled.p<{ baseFontFamily?: string }>`
  color: ${(p) => p.theme.colors.greyDarkest};
  font-family: ${(p) => p.baseFontFamily || 'Avenir, sans-serif'};
  font-weight: 800;
  line-height: 24px;
  margin: 0;
  max-height: 48px;
  min-height: 30px;
  overflow: hidden;
`;

const Preview = styled.p<{ baseFontFamily?: string }>`
  color: ${(p) => p.theme.colors.greyDarkest};
  font-size: 14px;
  line-height: 21px;
  margin: 0;
  word-break: break-word;
  padding-top: ${(p) => p.theme.space[1]}px;
  font-family: ${(p) => p.baseFontFamily || 'Avenir, sans-serif'};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const ReadMore = styled.span<{ baseFontFamily?: string }>`
  font-family: ${(p) => p.baseFontFamily || 'Avenir, sans-serif'};
  color: ${(p) => p.theme.colors.primary};
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
`;

const IconWrapper = styled(Flex)`
  ${({ theme }: any) => css`
    background-color: ${theme.colors.purpleLightest};
    border-radius: ${theme.radii[2]}px;
    height: 32px;
    padding: ${theme.space[2]}px;
    width: 32px;
    margin-right: ${theme.space[2]}px;
  `};
`;

const BookOpenPageIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.purple};
`;

type Props = {
  uid: string;
  educationId: number;
  title: string;
  previewText: string;
};

const Education: React.FC<Props> = ({ uid, educationId, title, previewText }) => {
  const [messageViewed, setMessageViewed] = useState(false);

  const handleEducationClick = () => onEducationMessageClicked({ id: educationId, title });

  const handleEducationViewed = () => {
    if (!messageViewed) {
      setMessageViewed(true);
      onEducationMessageViewed({ id: educationId, title });
    }
  };

  const ref = useIntersectionObserver(handleEducationViewed);

  const {
    config: {
      Theme: {
        typography: { baseFontFamily },
      },
    },
  } = useAppContext();

  return (
    <div ref={ref}>
      <UniversalLink to={`/education/${educationId}`} onClick={handleEducationClick} target="_blank" id={uid}>
        <Wrapper>
          <Flex flexDirection="row">
            <IconWrapper justifyContent="center" alignItems="center">
              <BookOpenPageIcon name="book-open-page-variant" size={24} />
            </IconWrapper>

            <Box>
              <Title baseFontFamily={baseFontFamily}>{title}</Title>
              <Preview baseFontFamily={baseFontFamily}>{previewText}</Preview>
              <ReadMore baseFontFamily={baseFontFamily}>Read More</ReadMore>
            </Box>
          </Flex>
        </Wrapper>
      </UniversalLink>
    </div>
  );
};

export const EducationMessage: React.FC<MessageProps> = ({ message, messageRef }) => {
  return (
    <>
      <DefaultMessage message={message} messageRef={messageRef} fromCurrentUser={false} />
      {message.education?.map(({ id, title, previewText }) => {
        const uid = uniqueId(title);
        return <Education key={uid} uid={uid} educationId={id} title={title} previewText={previewText} />;
      })}
    </>
  );
};
